import { AxiosResponse } from 'axios';

import { API } from '../../utils/axios';

import { APIResponseStatus } from '../types';
import {
  confirmUserAttribute,
  ConfirmUserAttributeInput,
  updateUserAttributes,
} from 'aws-amplify/auth';

export enum Gender {
  Female = 'female',
  Male = 'male',
}

export enum ForResourceType {
  Meeting = 'Meeting',
  Patient = 'Patient',
  PatientInvitation = 'PatientInvitation',
  PatientRegistration = 'PatientRegistration',
  Visit = 'Visit',
  VisitBooking = 'VisitBooking',
  VisitIntake = 'VisitIntake',
}

export enum Status {
  InProgress = 'in-progress',
}

export type Address = {
  use: AddressUse;
  line: string[];
  city: string;
  state: State;
  postalCode?: string;
  country: Country;
  postalcode?: string;
};

export enum Country {
  Us = 'US',
  Usa = 'USA',
}

export enum State {
  Calabarzon = 'Calabarzon',
  De = 'DE',
  Florida = 'Florida',
}

export enum AddressUse {
  Home = 'home',
}

export type Avatar = {
  resourceType: EncounterType;
  uuid: string;
  id: string;
  identifier: fhir4.Identifier[];
  parent: Parent;
  type: string;
  name: string;
  description: string;
  blob: Encounter;
  blobdata: Blobdata;
  url: string;
};

export type Encounter = {
  reference?: string;
  type: EncounterType;
  identifier: fhir4.Identifier;
  display?: string;
};

export enum EncounterType {
  Blob = 'Blob',
  Encounter = 'Encounter',
  File = 'File',
  Patient = 'Patient',
}

export type Blobdata = {
  resourceType: EncounterType;
  uuid: string;
  id: string;
  identifier: fhir4.Identifier[];
  bucket: string;
  key: string;
  blob: Blob;
  contentType: string;
  contentLength: number;
  file: Encounter;
  creator: any;
};

export type Blob = {
  ETag: string;
  ServerSideEncryption: string;
};

export type Parent = {
  reference: string;
  identifier: fhir4.Identifier;
};

export type Household = {
  resourceType: ResourceType;
  uuid: string;
  id: string;
  identifier: fhir4.Identifier[];
  type: string;
  actual: boolean;
  name: string;
  quantity: number;
  member: Member[];
};

export enum ResourceType {
  Group = 'Group',
  GroupMember = 'Group.member',
  Patient = 'Patient',
}

export type Member = {
  entity: Encounter;
  relationship: Relationship;
  resourceType: ResourceType;
  id: string;
  elementVersion: number;
  registration?: Registration[];
  uuid?: string;
  identifier?: fhir4.Identifier[];
  name?: fhir4.HumanName[];
  telecom?: fhir4.Identifier[];
  gender?: Gender;
  birthDate?: Date;
  address?: Address[];
  preference?: MemberPreference;
};

export type Relationship = {
  text: string;
};

export type MemberPreference = {
  notification?: Notification;
};

export type Notification = {
  general: General;
};

export type General = {
  app: boolean;
  email: boolean;
  sms: boolean;
};

export type Registration = {
  resourceType: ForResourceType;
  uuid: string;
  id: string;
  identifier: fhir4.Identifier[];
  status: Status;
  intent: Intent;
  workflowType: WorkflowType;
  step: Step[];
  process: fhir4.Reference[];
  timestamp: Date;
  profile: Profile;
  patient: Encounter;
  invitation: Invitation[];
};

export enum Intent {
  Plan = 'plan',
}

export type Invitation = {
  resourceType: ForResourceType;
  uuid: string;
  id: string;
  status: Status;
  intent: Intent;
  workflow: fhir4.Reference;
  invitee: Encounter;
  inviter: Encounter;
  inviteCode: string;
  relationship: Relationship;
  expiresAt: Date;
};

export type Profile = {
  address: Address[];
  birthdate: Date;
  element_status: string;
  element_type: string;
  gender: Gender;
  is_minor?: boolean;
  name: fhir4.HumanName[];
  telecom: fhir4.Identifier[];
};

export enum Step {
  CreateInvitation = 'create-invitation',
  CreateInviteSession = 'create-inviteSession',
  CreatePatient = 'create-patient',
  CreateProfile = 'create-profile',
  CreateUser = 'create-user',
  SendInvitation = 'send-invitation',
}

export enum WorkflowType {
  PatientRegistration = 'patient-registration',
}

export type PractitionerPreferences = {
  timezone?: string;
  notification?: PractitionerNotificationSettings;
  system?: {
    theme: 'light' | 'dark' | 'system';
  };
};

export type PractitionerNotificationSettings = {
  event_1: {
    sms: boolean;
    email: boolean;
  };
  event_2: {
    sms: boolean;
    email: boolean;
  };
  event_3: {
    sms: boolean;
    email: boolean;
  };
  event_4: {
    sms: boolean;
    email: boolean;
  };
  event_5: {
    sms: boolean;
    email: boolean;
  };
  event_6: {
    sms: boolean;
    email: boolean;
  };
  event_7: {
    sms: boolean;
    email: boolean;
  };
  event_8: {
    sms: boolean;
    email: boolean;
  };
  event_9: {
    sms: boolean;
    email: boolean;
  };
  event_10: {
    sms: boolean;
    email: boolean;
  };
  event_11: {
    sms: boolean;
    email: boolean;
  };
};

export interface Fhir4ExtendedOrganization extends fhir4.Organization {
  id?: string;
  uuid?: string;
  organizationType?: string;
  preference?: {
    system?: {
      units: 'metric' | 'imperial';
    };
  };
}

export type Plan = {
  id: string;
  name: string;
  active: boolean;
  plan_id: number;
  household: boolean;
  timestamp: Date;
  element_id: number;
  description: string;
  resource_id: number;
  element_type: string;
  organization: fhir4.Reference;
  resource_uuid: string;
  element_status: string;
  visitDiscount: number;
  element_version: number;
  domainresource_id: number;
  cancellation_discount: number;
};

export interface ClinicOrganization extends Fhir4ExtendedOrganization {
  resourceType: 'Organization';
  name: string;
  preference?: any;
  plan?: Array<ClinicPlanReference>;
  logo?: Array<FilePractitionerObject>;
  logoVisibleToPatients?: boolean;
  employeeCount?: { total?: number; active?: number; invited?: number };
}

export interface FilePractitionerObject {
  resourceType: 'File';
  uuid: string;
  id: string;
  identifier: Array<{ system: string; value: string }>;
  parent?: { reference: string; identifier: { system: string; value: string } };
  type?: string;
  name?: string;
  description?: string;
  blob?: {
    reference: string;
    type: string;
    identifier: { system: string; value: string };
  };
  blobData?: {
    resourceType: string;
    uuid: string;
    id: string;
    identifier: Array<{ system: string; value: string }>;
    bucket: string;
    key: string;
    blob: { ETag: string; serverSideEncryption: string };
    contentType: string;
    contentLength: number;
    file: {
      reference: string;
      type: string;
      identifier: { system: string; value: string };
    };
  };
  url: string;
}

export interface ClinicPlanReference extends fhir4.Reference {
  default?: boolean;
  name?: string;
}

export type Patient = {
  readonly resourceType: 'Patient';
  uuid: string;
  id: string;
  identifier: fhir4.Identifier[];
  name: fhir4.HumanName[];
  telecom: fhir4.ContactPoint[];
  gender: string;
  birthDate: Date;
  address: fhir4.Address[];
  managingOrganization: fhir4.Reference;
  preference: PractitionerPreferences;
  elementVersion: number;
  loginCount: number;
  avatar?: Avatar[];
  household?: Household;
  plan?: Plan[];
  organization?: ClinicOrganization[];
  headOfHousehold?: Patient[];
  relationship?: Relationship[];
};

class PatientService {
  static async updateUser(params: any, uuid: string, callback: (success: boolean) => void) {
    try {
      const res = await API.put<any, AxiosResponse<Patient>>(
        `/v1/resources/Patient/${uuid}`,
        params,
      );

      callback(true);
      return res.data;
    } catch (error) {
      callback(false);
      throw error;
    }
  }

  static sendVerificationCode(params: any) {
    return API.post<any, AxiosResponse<APIResponseStatus>>(
      `/v1/resources/Telecom/verification`,
      params,
    ).then((res) => res.data);
  }

  static verifyPhoneCode(params: { verCodeResUUID: string; verCode: string }) {
    return API.post<any, AxiosResponse<APIResponseStatus>>(
      `/v1/resources/Telecom/verification/${params.verCodeResUUID}`,
      {
        verificationCode: params.verCode,
      },
    ).then((res) => res.data);
  }

  static async verifyEmailCode({
    userAttributeKey,
    confirmationCode,
    params,
  }: ConfirmUserAttributeInput & { params: any }) {
    await confirmUserAttribute({
      userAttributeKey,
      confirmationCode,
    });

    return API.post<any, AxiosResponse<APIResponseStatus>>(`/v1/resources/Patient`, params).then(
      (res) => res.data,
    );
  }

  static editPhone(params: any) {
    return API.post<any, AxiosResponse<APIResponseStatus>>(`/v1/resources/Patient`, params).then(
      (res) => res.data,
    );
  }

  static editEmail(email: string) {
    return updateUserAttributes({ userAttributes: { email } });
  }

  static getSmsConsent(patientId: string) {
    return API.get<any, AxiosResponse<APIResponseStatus>>(
      `/v1/resources/Patient/sms-consent/${patientId}`,
    ).then((res) => res.data);
  }

  static updateSmsConsent(patientId: string, smsConsent: boolean) {
    return API.post<any, AxiosResponse<APIResponseStatus>>(
      `/v1/resources/Patient/sms-consent/${patientId}`,
      {
        sms_consent: smsConsent,
      },
    ).then((res) => res.data);
  }
}

export default PatientService;
